/* Nerve Center v4 — LIVE ELECTRICAL TRAFFIC LAYER (data plane).
   Normalized activity event contract + ingestion with backpressure,
   short-window aggregation into bounded visual bursts, value tiers,
   truth guardrails: ONLY positively classified BUY/SELL (confidence ≥ 0.7)
   drives direction. UNKNOWN contributes to neutral intensity only. No
   qualifying data = no directional traffic (data fallback hierarchy:
   L1 classified events → directional · L2 aggregated buy/sell volume →
   weighted bursts · L3 activity/24H only → neutral signals). */

export type TrafficSide = "BUY" | "SELL" | "UNKNOWN";
export type TrafficEvent = {
  entity_id: string; token_id?: string; event_type?: string; side: TrafficSide;
  amount?: number; usd_value?: number; timestamp: number; chain_id?: string;
  tx_hash?: string; source: string; confidence?: number;
};
export type TrafficBurst = {
  slug: string; dir: 1 | -1; t0: number; dur: number;
  tier: number; density: number; conc: number; seed: number;
  _started?: boolean; _done?: boolean;
};
export type NucleusPulse = { t0: number; power: number; n: number; exit: number[] | null };

/* founder-tuned thresholds (option c): NORMAL from $4.5k single trade so
   genuine live Kraken flow produces regular full-bundle currents */
const tierOf = (usd: number) => (usd >= 2e6 ? 3 : usd >= 250_000 ? 2 : usd >= 4_500 ? 1 : 0);
const slugHash = (s: string) => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return h >>> 0;
};

export const createTrafficEngine = () => {
  const eng = {
    level: 3,
    seedN: 0,
    slowF: 1,
    lastClassifiedGlobal: -1e9,
    queue: [] as TrafficEvent[],
    bursts: [] as TrafficBurst[],
    fireFlags: new Map<string, { kind: string; until: number }>(),
    pulse: { t0: -1e9, power: 0, n: 0, exit: null } as NucleusPulse,
    suppress: new Set<string>(),
    lastClassifiedAt: new Map<string, number>(),
    lastFlush: 0,
    telemetry: [] as string[],
    stats: { in: 0, buy: 0, sell: 0, unk: 0, dropped: 0, ghosts: 0 },

    tel(msg: string) {
      eng.telemetry.push(msg);
      if (eng.telemetry.length > 40) eng.telemetry.splice(0, 15);
    },

    ingest(events: TrafficEvent[], visible: Set<string>, now: number) {
      for (const ev of events || []) {
        eng.stats.in++;
        if (!ev || !ev.entity_id || !Number.isFinite(ev.timestamp)) { eng.stats.dropped++; continue; }
        /* normalize epoch-ms timestamps into the perf clock (latency-aware) */
        const ts = ev.timestamp > 1e12 ? now - (Date.now() - ev.timestamp) : ev.timestamp;
        if (now - ts > 20_000) { eng.stats.dropped++; continue; } // currentness > playback
        /* HARD GUARDRAIL: never guess direction from unclassified activity */
        const side: TrafficSide =
          (ev.side === "BUY" || ev.side === "SELL") && (ev.confidence ?? 0) >= 0.7 ? ev.side : "UNKNOWN";
        const slug = ev.entity_id.startsWith("protocol:") ? ev.entity_id.slice(9) : null;
        if (!slug) { eng.stats.dropped++; eng.tel(`DROP non-canonical id ${ev.entity_id}`); continue; }
        if (!visible.has(slug)) {
          eng.stats.ghosts++;
          eng.tel(`SKIP ${side} ${slug} — entity not visible, no ghost signal`);
          continue;
        }
        if (side !== "UNKNOWN") {
          eng.lastClassifiedAt.set(slug, now);
          eng.lastClassifiedGlobal = now;
        }
        if (eng.queue.length >= 400) { eng.queue.splice(0, 120); eng.stats.dropped += 120; } // backpressure
        eng.queue.push({ ...ev, side });
        if (side === "BUY") eng.stats.buy++; else if (side === "SELL") eng.stats.sell++; else eng.stats.unk++;
        eng.tel(`${side} ${slug} $${Math.round(ev.usd_value ?? 0).toLocaleString()} conf=${ev.confidence ?? "-"} src=${ev.source}`);
      }
    },

    /* aggregate raw events into bounded visual bursts (1.5s windows) */
    flush(now: number, slotOf: Map<string, number>, maxConcurrent: number, lenOf?: Map<string, number>) {
      eng.level = now - eng.lastClassifiedGlobal < 30_000 ? 1 : 3;
      eng.suppress.clear();
      eng.lastClassifiedAt.forEach((t, s) => { if (now - t < 8000) eng.suppress.add(s); });
      if (now - eng.lastFlush < 1500) return;
      eng.lastFlush = now;
      eng.bursts = eng.bursts.filter((b) => now - b.t0 < b.dur + 500);
      if (!eng.queue.length) return;
      const agg = new Map<string, any>();
      for (const ev of eng.queue) {
        const slug = ev.entity_id.slice(9);
        const a = agg.get(slug) || { buyUsd: 0, sellUsd: 0, buyN: 0, sellN: 0, unkN: 0, maxBuy: 0, maxSell: 0 };
        const v = Math.max(0, ev.usd_value ?? 0);
        if (ev.side === "BUY") { a.buyUsd += v; a.buyN++; a.maxBuy = Math.max(a.maxBuy, v); }
        else if (ev.side === "SELL") { a.sellUsd += v; a.sellN++; a.maxSell = Math.max(a.maxSell, v); }
        else a.unkN++;
        agg.set(slug, a);
      }
      eng.queue.length = 0;
      /* §frequency-vs-value: trade COUNT drives how often signals fire,
         trade VALUE drives amplitude (strands/brightness/reaction). Micro
         trades = frequent fine low-amplitude firings — never invisible,
         never one thick burst. Majors stay bounded (≤maxConcurrent). */
      let majors = eng.bursts.filter((b) => b.tier > 0 && now - b.t0 < b.dur).length;
      let micros = eng.bursts.filter((b) => b.tier === 0 && now - b.t0 < b.dur + 1300).length;
      const microCap = maxConcurrent * 3 + 1;
      agg.forEach((a, slug) => {
        if (!slotOf.has(slug)) return;
        const mk = (dir: 1 | -1, usd: number, n: number, maxV: number, delay: number) => {
          if (!n) return;
          /* tier from the largest single trade; aggregated flow may promote
             (÷5) ONLY when the largest trade isn't deeply micro — many
             genuinely tiny trades stay MICRO-frequency (§K, §7: never
             artificially promote micros to make the scene exciting) */
          const base = tierOf(maxV);
          const tier = maxV >= 2_000 ? Math.max(base, tierOf(usd / 5)) : base;
          const seed = () => (slugHash(slug) + ++eng.seedN * 9973) >>> 0;
          const lf = lenOf?.get(slug) ?? 1;
          if (tier === 0) {
            const fires = Math.min(6, Math.max(1, Math.round(Math.sqrt(n) * 1.4)));
            let made = 0;
            for (let i = 0; i < fires && micros < microCap; i++) {
              eng.bursts.push({
                slug, dir, t0: now + delay + i * (1250 / fires) + (i % 3) * 55,
                /* §micro-speed: 600-850ms, length-aware — trackable at normal
                   playback: origin → travel → arrival must be followable */
                dur: Math.min(880, Math.max(580, (620 + 45 * (i % 5)) * lf)) * eng.slowF, tier: 0,
                density: Math.min(1, 0.25 + n / 24),
                conc: Math.min(0.8, 0.2 + Math.sqrt(usd / Math.max(1, n) / 60_000)),
                seed: seed(),
              });
              micros++; made++;
            }
            if (made) eng.tel(`MICRO ${dir === 1 ? "IN·BUY" : "OUT·SELL"} ${slug} n=${n} $${Math.round(usd).toLocaleString()} → ${made} fine firings`);
            return;
          }
          if (majors >= maxConcurrent) return;
          const density = Math.min(1, 0.3 + Math.sqrt(n) / 6);
          const conc = Math.min(1.6, 0.35 + Math.sqrt(usd / Math.max(1, n) / 60_000));
          /* travel time founder-locked, length-aware (consistent apparent
             velocity): origin → travel → arrival stays visually trackable */
          eng.bursts.push({ slug, dir, t0: now + delay, dur: Math.min(1350, Math.min(1100, 660 + 130 * tier + 200 * density) * lf) * eng.slowF, tier, density, conc, seed: seed() });
          majors++;
          eng.tel(`BURST ${dir === 1 ? "IN·BUY" : "OUT·SELL"} ${slug} n=${n} $${Math.round(usd).toLocaleString()} tier=${["MICRO", "NORMAL", "LARGE", "EXCEPTIONAL"][tier]} dens=${density.toFixed(2)} conc=${conc.toFixed(2)}`);
        };
        mk(1, a.buyUsd, a.buyN, a.maxBuy, 0);
        mk(-1, a.sellUsd, a.sellN, a.maxSell, a.buyN ? 650 : 0);
        if (a.unkN) eng.tel(`NEUTRAL ${slug} — ${a.unkN} unclassified, intensity only, no direction`);
      });
    },

    fireFlag(slug: string, now: number) {
      const f = eng.fireFlags.get(slug);
      return f && now < f.until ? f.kind : "";
    },
    setFlag(slug: string, kind: string, now: number, ms = 600) {
      eng.fireFlags.set(slug, { kind, until: now + ms });
    },
    /* merge near-simultaneous BUY arrivals into one richer bounded pulse */
    mergePulse(now: number, power: number, exit: number[] | null) {
      if (now - eng.pulse.t0 < 450 && eng.pulse.power > 0) {
        eng.pulse.power = Math.min(1.6, eng.pulse.power + power * 0.6);
        eng.pulse.n++;
        if (exit) eng.pulse.exit = exit;
      } else eng.pulse = { t0: now, power: Math.min(1.2, power), n: 1, exit };
    },
  };
  return eng;
};
export type TrafficEngine = ReturnType<typeof createTrafficEngine>;

/* ── DEV/QA fixtures (§REQUIRED TEST FIXTURES) — injected only via
   ?nerve_fixture= or the nerve:traffic dev event. Never in production. */
export const fixtureEvents = (key: string, slugs: string[], now: number): TrafficEvent[] => {
  const mk = (slug: string, side: TrafficSide, usd: number, conf = 0.95): TrafficEvent => ({
    entity_id: `protocol:${slug}`, side, usd_value: usd, timestamp: now,
    source: "fixture", confidence: conf, event_type: "swap",
  });
  const s = (i: number) => slugs[i % slugs.length];
  switch ((key || "").toUpperCase()) {
    case "A": return Array.from({ length: 10 }, (_, i) => mk(s(i % 3), "BUY", 25_000));
    case "B": return Array.from({ length: 10 }, (_, i) => mk(s(i % 3), "SELL", 25_000));
    case "C": return [
      ...Array.from({ length: 5 }, (_, i) => mk(s(i), "BUY", 40_000)),
      ...Array.from({ length: 5 }, (_, i) => mk(s(i + 1), "SELL", 30_000)),
    ];
    case "D": return Array.from({ length: 100 }, () => mk(s(0), "BUY", 500));
    case "E": return [mk(s(1), "BUY", 3_000_000)];
    case "F": return [
      ...Array.from({ length: 5 }, (_, i) => mk(s(i % 2), "UNKNOWN", 15_000)),
      ...Array.from({ length: 5 }, (_, i) => ({ ...mk(s(i % 2), "BUY", 15_000), confidence: 0.3 })),
    ];
    case "G": return Array.from({ length: 6 }, () => mk("ghost-protocol-not-visible", "BUY", 90_000));
    case "H": return [
      ...Array.from({ length: 4 }, (_, i) => mk(s(i), "BUY", 60_000)),
      ...Array.from({ length: 4 }, (_, i) => mk(s(i + 2), "SELL", 45_000)),
    ];
    /* high-throughput burst — aggregation/backpressure must compress this
       into bounded multi-strand pulses, never 250 stacked animations */
    case "BURST": return Array.from({ length: 250 }, (_, i) =>
      mk(s(i % slugs.length), i % 3 === 2 ? "SELL" : "BUY", 4_000 + (i % 9) * 6_000));
    /* micro-traffic — frequent genuine small trades: the field must show
       fine high-frequency low-amplitude conduction, not silence */
    case "M": return Array.from({ length: 24 }, (_, i) =>
      mk(s(i % Math.min(5, slugs.length)), i % 3 === 2 ? "SELL" : "BUY", 150 + (i % 7) * 400));
    default: return [];
  }
};
